import { useEffect, useRef, useState } from "react";
import { Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common";
import { fiscal } from "@/fiscal";
import { cn } from "@/lib/utils";

const fmtNcm = (c) => (c && c.length === 8 ? `${c.slice(0, 4)}.${c.slice(4, 6)}.${c.slice(6)}` : c);
const fmtCest = (c) => (c && c.length === 7 ? `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5)}` : c);

// Campo com busca enquanto digita (código ou descrição) e lista de sugestões.
function BuscaFiscal({ label, hint, value, valueLabel, placeholder, maxDigits, buscar, onPick, onClear, testId, extra }) {
  const [texto, setTexto] = useState("");
  const [aberto, setAberto] = useState(false);
  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [aviso, setAviso] = useState("");
  const [ativo, setAtivo] = useState(0);
  const caixa = useRef(null);
  const seq = useRef(0);
  const buscarRef = useRef(buscar);
  buscarRef.current = buscar;

  useEffect(() => {
    if (!aberto) return undefined;
    const termo = texto.trim();
    const n = ++seq.current;
    const timer = setTimeout(async () => {
      setCarregando(true);
      setAviso("");
      try {
        const r = await buscarRef.current(termo);
        if (n === seq.current) { setItens(r); setAtivo(0); }
      } catch (e) {
        if (n === seq.current) { setItens([]); setAviso(e.message); }
      } finally {
        if (n === seq.current) setCarregando(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [texto, aberto]);

  useEffect(() => {
    const fora = (e) => caixa.current && !caixa.current.contains(e.target) && setAberto(false);
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  const escolher = (it) => { onPick(it); setTexto(""); setAberto(false); };
  const tecla = (e) => {
    if (!aberto || !itens.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setAtivo((a) => Math.min(a + 1, itens.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setAtivo((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); escolher(itens[ativo]); }
    else if (e.key === "Escape") setAberto(false);
  };
  // Digitou só números com o tamanho certo: aceita mesmo sem escolher na lista
  const aoSair = () => {
    const d = texto.replace(/\D/g, "");
    if (d.length === maxDigits && d === texto.replace(/[.\s]/g, "")) onPick({ codigo: d });
  };

  return (
    <Field label={label} hint={hint} className="col-span-2">
      <div ref={caixa} className="relative">
        {value && !aberto ? (
          <button type="button" onClick={() => { setTexto(""); setAberto(true); }}
            className="flex min-h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-left text-sm"
            data-testid={`${testId}-value`}>
            <span className="font-mono font-semibold">{valueLabel || value}</span>
            <span className="line-clamp-2 flex-1 text-muted-foreground">{extra}</span>
            <X className="h-4 w-4 shrink-0 text-muted-foreground hover:text-foreground" aria-label="Limpar"
              onClick={(e) => { e.stopPropagation(); onClear(); }} data-testid={`${testId}-clear`} />
          </button>
        ) : (
          <Input autoFocus={aberto} value={texto} placeholder={placeholder} onFocus={() => setAberto(true)}
            onChange={(e) => { setTexto(e.target.value); setAberto(true); }} onKeyDown={tecla} onBlur={aoSair}
            data-testid={`${testId}-input`} />
        )}
        {aberto && (
          <div className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-md border bg-popover shadow-lg" data-testid={`${testId}-list`}>
            {carregando && <p className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Buscando…</p>}
            {!carregando && aviso && <p className="px-3 py-2 text-sm text-amber-600">{aviso}</p>}
            {!carregando && !aviso && !itens.length && (
              <p className="px-3 py-2 text-sm text-muted-foreground">{texto.trim().length < 2 ? "Digite o código ou parte da descrição" : "Nada encontrado"}</p>
            )}
            {itens.map((it, i) => (
              <button key={it.codigo} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => escolher(it)}
                onMouseEnter={() => setAtivo(i)}
                className={cn("block w-full border-b px-3 py-2 text-left text-sm last:border-0", i === ativo && "bg-accent")}>
                <span className="block">{it.descricao}</span>
                <span className="font-mono text-xs text-muted-foreground">Código: {it.formatado || fmtNcm(it.codigo)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </Field>
  );
}

// NCM com busca na tabela oficial e CEST filtrado pelo NCM (Convênio ICMS 142/18).
export function NcmCestFields({ f, set }) {
  const [ncmDesc, setNcmDesc] = useState("");
  const [cestDesc, setCestDesc] = useState("");
  const [doNcm, setDoNcm] = useState(null); // CESTs possíveis para o NCM escolhido

  useEffect(() => {
    let vivo = true;
    setNcmDesc("");
    if (f.ncm?.length === 8) fiscal.ncm(f.ncm).then((d) => vivo && setNcmDesc(d.descricao)).catch(() => vivo && setNcmDesc("Não encontrado na tabela NCM vigente — confira o código"));
    return () => { vivo = false; };
  }, [f.ncm]);

  useEffect(() => {
    let vivo = true;
    setDoNcm(null);
    if (f.ncm?.length !== 8) return undefined;
    fiscal.cest({ ncm: f.ncm }).then((r) => {
      if (!vivo) return;
      setDoNcm(r.itens);
      if (r.itens.length === 1 && !f.cest) { set("cest", r.itens[0].codigo); setCestDesc(r.itens[0].descricao); }
    }).catch(() => vivo && setDoNcm(null));
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.ncm]);

  useEffect(() => {
    if (!f.cest) { setCestDesc(""); return; }
    const achado = (doNcm || []).find((c) => c.codigo === f.cest);
    if (achado) setCestDesc(achado.descricao);
    else if (!cestDesc) fiscal.cest({ busca: f.cest }).then((r) => setCestDesc(r.itens.find((c) => c.codigo === f.cest)?.descricao || "")).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.cest, doNcm]);

  const buscarNcm = (t) => (t.length < 2 ? Promise.resolve([]) : fiscal.buscarNcm(t));
  const buscarCest = (t) => {
    if (doNcm && !t) return Promise.resolve(doNcm);
    if (t.length < 2 && !(f.ncm?.length === 8)) return Promise.resolve([]);
    return fiscal.cest({ ncm: f.ncm?.length === 8 && !t ? f.ncm : "", busca: t }).then((r) => r.itens);
  };

  const cestHint = f.ncm?.length !== 8
    ? "7 dígitos · só para produtos na substituição tributária (Convênio ICMS 142/18). Escolha o NCM primeiro para ver as opções."
    : doNcm === null ? "Carregando opções para este NCM…"
    : doNcm.length === 0 ? "Este NCM não aparece no Convênio 142/18: normalmente fica sem CEST (sem substituição tributária)."
    : doNcm.length === 1 ? "Preenchido pelo NCM. Confira se a descrição bate com o produto."
    : `${doNcm.length} opções para este NCM: escolha a que descreve o produto.`;

  return (
    <>
      <BuscaFiscal label="NCM" testId="product-ncm"
        hint="Digite o código ou parte da descrição (ex.: refrigerante, 2202). Obrigatório para emitir NFC-e/NF-e."
        value={f.ncm} valueLabel={fmtNcm(f.ncm)} extra={ncmDesc} maxDigits={8}
        placeholder="Pesquise a tabela NCM" buscar={buscarNcm}
        onPick={(it) => { if (it.codigo !== f.ncm) { set("ncm", it.codigo); set("cest", ""); setCestDesc(""); } if (it.descricao) setNcmDesc(it.descricao); }}
        onClear={() => { set("ncm", ""); set("cest", ""); }} />
      <BuscaFiscal label="CEST" testId="product-cest" hint={cestHint}
        value={f.cest} valueLabel={fmtCest(f.cest)} extra={cestDesc} maxDigits={7}
        placeholder={doNcm?.length ? "Escolha o CEST deste NCM" : "Pesquise o CEST (opcional)"} buscar={buscarCest}
        onPick={(it) => { set("cest", it.codigo); if (it.descricao) setCestDesc(it.descricao); }}
        onClear={() => { set("cest", ""); setCestDesc(""); }} />
    </>
  );
}
